import { useEffect, useState } from 'react';

import {
  CLASS_COLOR_CUSTOM_SELECT_VALUE,
  colorOptions,
  getPresetClassColorMatch,
} from '~shared/constants/colors';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

export interface ClassColorPickerProps {
  value: string | undefined;
  onChange: (value: string) => void;
  selectTriggerId: string;
  inputId: string;
  /** When this changes (e.g. edited class id), internal "prefer custom" state resets */
  resetPreferCustomKey?: string;
}

export function ClassColorPicker({
  value: color,
  onChange,
  selectTriggerId,
  inputId,
  resetPreferCustomKey,
}: ClassColorPickerProps) {
  /** Lets user pick "Custom" while the hex still matches a preset (otherwise Select stays on preset). */
  const [colorSelectPreferCustom, setColorSelectPreferCustom] = useState(false);

  useEffect(() => {
    if (resetPreferCustomKey === undefined) return;
    setColorSelectPreferCustom(false);
  }, [resetPreferCustomKey]);

  /** Avoid uncontrolled→controlled warning if value is ever undefined during hydration */
  const classColorInputValue = typeof color === 'string' ? color : '';
  const presetClassColor = getPresetClassColorMatch(color);
  const classColorSelectValue =
    presetClassColor && !colorSelectPreferCustom
      ? presetClassColor.value
      : CLASS_COLOR_CUSTOM_SELECT_VALUE;
  const showCustomColorInput = !presetClassColor || colorSelectPreferCustom;

  return (
    <div className='w-full space-y-2'>
      <Label htmlFor={selectTriggerId}>Color</Label>
      <div className='flex w-full flex-wrap items-center gap-2'>
        <Select
          value={classColorSelectValue}
          onValueChange={(value) => {
            if (value === CLASS_COLOR_CUSTOM_SELECT_VALUE) {
              setColorSelectPreferCustom(true);
              return;
            }
            setColorSelectPreferCustom(false);
            onChange(value);
          }}
        >
          <SelectTrigger id={selectTriggerId} className='w-full sm:w-[200px]'>
            <SelectValue placeholder='Select a color'>
              <div className='flex items-center space-x-2'>
                {color ? (
                  <div
                    className='size-4 shrink-0 rounded-full'
                    style={{ backgroundColor: color }}
                  />
                ) : null}
                <span className='truncate'>
                  {colorSelectPreferCustom
                    ? 'Custom'
                    : (presetClassColor?.label ?? 'Custom')}
                  {showCustomColorInput && color ? (
                    <span className='ml-1 text-muted-foreground'>
                      ({color})
                    </span>
                  ) : null}
                </span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {colorOptions.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                <div className='flex items-center space-x-2'>
                  <div
                    className='size-4 rounded-full'
                    style={{ backgroundColor: c.value }}
                  />
                  <span>{c.label}</span>
                </div>
              </SelectItem>
            ))}
            <SelectItem value={CLASS_COLOR_CUSTOM_SELECT_VALUE}>
              <span>Custom…</span>
            </SelectItem>
          </SelectContent>
        </Select>
        {showCustomColorInput ? (
          <Input
            id={inputId}
            className='w-[140px] font-mono text-sm'
            value={classColorInputValue}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder='#RRGGBB'
            maxLength={7}
            spellCheck={false}
            aria-label='Custom hex color'
          />
        ) : null}
      </div>
    </div>
  );
}
